// src/pages/DataTeam/DataTeamLayout.js

import React, { useContext, useState, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import axios from 'axios';
import SidebarAccordion from '../../components/SidebarAccordion';
import PageLayout from '../PageLayout';
import { SidebarContext } from '../../context/SidebarContext';
import { isSeriesActive } from '../../utils/helpers';
import './DataTeamLayout.css';
import {
  FaSearch,
  FaChartLine,
  FaFileAlt,
  FaDownload,  // For "Download per student" icon
  FaArchive
} from 'react-icons/fa';

function DataTeamLayout() {
  const sidebarContext = useContext(SidebarContext) || {};
  const { sidebarOpen = false } = sidebarContext;
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios
      .get('/api/exam-series')
      .then((res) => {
        setSeriesList(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Error fetching exam series.');
        setLoading(false);
      });
  }, []);

  // For "Browse by Series" - show only active series
  const activeSeries = seriesList.filter(series => isSeriesActive(series));
  // Past series' work doesn't disappear once a series ends - it's just not
  // part of the day-to-day OSM/RLP/CAD browsing lists above. Archives is
  // the dedicated place to reach it: one entry per expired series, each
  // expanding to the same OSM/RLP/CAD browsing options, scoped to that
  // series' id.
  const expiredSeries = seriesList.filter(series => series.expired);
  const emptySeriesChildren = [
    {
      title: loading ? 'Loading...' : 'Nothing to show here',
      path: '#',
      icon: <FaFileAlt />
    }
  ];

  const seriesChildrenFor = (examTypeSlug) =>
    activeSeries.length > 0
      ? activeSeries.map((series) => ({
          title: series.name,
          path: `/data-team/${examTypeSlug}/series/${series.id}`,
          icon: <FaFileAlt />
        }))
      : emptySeriesChildren;

  // Separate top-level sections per exam type: OSM and RLP each browse by
  // series (marksheet + scoring guide per centre), CAD splits into browsing
  // per exam centre (the bundled candidate folders) and browsing/searching
  // per individual candidate.
  const menuItems = [
    {
      title: 'OSM',
      icon: <FaFileAlt />,
      children: seriesChildrenFor('osm')
    },
    {
      title: 'RLP',
      icon: <FaFileAlt />,
      children: seriesChildrenFor('rlp')
    },
    {
      title: 'CAD',
      icon: <FaFileAlt />,
      children: [
        {
          title: 'Browse per Candidate',
          path: '/data-team/cad/candidates',
          icon: <FaSearch />
        },
        ...(activeSeries.length > 0
          ? activeSeries.map((series) => ({
              title: `Browse per Centre - ${series.name}`,
              path: `/data-team/cad/centres/${series.id}`,
              icon: <FaDownload />
            }))
          : emptySeriesChildren)
      ]
    },
    {
      title: 'Reports',
      icon: <FaChartLine />,
      children: [
        {
          title: 'Deployment List',
          path: '/data-team/reports/deployment-list',
          icon: <FaFileAlt />
        }
      ]
    },
    {
      // One folder per expired series; each expands to OSM/RLP/CAD, and
      // CAD itself expands further to Browse per Candidate (search/download
      // an individual candidate's folder) and Browse by Centre (download
      // the bundled folders for a whole exam centre) - the same two CAD
      // access patterns available for active series, just scoped to a
      // specific archived series.
      title: 'Archives',
      icon: <FaArchive />,
      children: expiredSeries.length > 0
        ? expiredSeries.map((series) => ({
            title: series.name,
            children: [
              { title: 'OSM', path: `/data-team/osm/series/${series.id}`, icon: <FaFileAlt /> },
              { title: 'RLP', path: `/data-team/rlp/series/${series.id}`, icon: <FaFileAlt /> },
              {
                title: 'CAD',
                children: [
                  { title: 'Browse per Candidate', path: '/data-team/cad/candidates', icon: <FaSearch /> },
                  { title: 'Browse by Centre', path: `/data-team/cad/centres/${series.id}`, icon: <FaDownload /> }
                ]
              }
            ]
          }))
        : emptySeriesChildren
    }
  ];

  return (
    <div className="data-team-layout">
      <div className={`data-team-layout-body ${sidebarOpen ? 'sidebar-open' : ''}`}>
        {error && <div className="text-danger px-3">{error}</div>}
        <SidebarAccordion menuItems={menuItems} treeStyle />
        <div className="main-content">
          <PageLayout>
            <Outlet />
          </PageLayout>
        </div>
      </div>
    </div>
  );
}

export default DataTeamLayout;
